import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, ChevronLeft, Package, FileCode, AlertCircle, CheckCircle,
  Info, Clock, RefreshCw, Loader2, FileArchive, Server, Monitor,
  History, Trash2, ShieldCheck, Settings, FolderOpen, RotateCcw,
  Download, Terminal, Copy, Check, AlertTriangle, Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { actualizacionesAPI } from '../api';

const ActualizarSistema = () => {
  const navigate = useNavigate();
  const [versionActual, setVersionActual] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [backups, setBackups] = useState([]);
  const [diagnostico, setDiagnostico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [crearBackup, setCrearBackup] = useState(true);
  const [resultado, setResultado] = useState(null);
  const [progress, setProgress] = useState(0);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [activeTab, setActiveTab] = useState('aplicar');

  const loadData = useCallback(async () => {
    try {
      const [versionRes, historialRes, diagnosticoRes, backupsRes] = await Promise.all([
        actualizacionesAPI.getVersionActual(),
        actualizacionesAPI.getHistorial(),
        actualizacionesAPI.getDiagnostico(),
        actualizacionesAPI.getBackups()
      ]);
      setVersionActual(versionRes.data);
      setHistorial(historialRes.data.actualizaciones || []);
      setDiagnostico(diagnosticoRes.data);
      setBackups(backupsRes.data.backups || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos del sistema",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast({
        title: "Error",
        description: "Solo se permiten archivos ZIP",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    setAnalisis(null);
    setResultado(null);
    setUploading(true);
    setProgress(30);

    try {
      const formData = new FormData();
      formData.append('archivo', file);
      
      const response = await actualizacionesAPI.analizar(formData);
      setAnalisis(response.data);
      setProgress(100);
      
      toast({
        title: "Paquete analizado",
        description: `${response.data.total_archivos} archivos encontrados`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error analizando el paquete",
        variant: "destructive"
      });
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleAplicar = async () => {
    if (!selectedFile) return;

    setApplying(true);
    setProgress(0);
    setResultado(null);

    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);
      
      setProgress(20);
      
      const response = await actualizacionesAPI.aplicar(formData, crearBackup);
      
      setProgress(100);
      setResultado(response.data);
      
      if (response.data.exito) {
        toast({
          title: "✅ Actualización aplicada",
          description: response.data.mensaje
        });
        loadData();
      } else {
        toast({
          title: "⚠️ Actualización con errores",
          description: response.data.mensaje,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error aplicando actualización",
        variant: "destructive"
      });
    } finally {
      setApplying(false);
    }
  };

  const handleRestaurarBackup = async (nombreBackup) => {
    if (!window.confirm(`¿Está seguro de restaurar el backup ${nombreBackup}?\n\nEsto sobrescribirá los archivos actuales.`)) {
      return;
    }
    
    try {
      const response = await actualizacionesAPI.restaurarBackup(nombreBackup);
      if (response.data.exito) {
        toast({
          title: "✅ Backup restaurado",
          description: response.data.mensaje
        });
        loadData();
      } else {
        toast({
          title: "⚠️ Restauración con errores",
          description: response.data.mensaje,
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error restaurando backup",
        variant: "destructive"
      });
    }
  };

  const handleLimpiarTemporales = async () => {
    try {
      const response = await actualizacionesAPI.limpiarTemporales();
      toast({
        title: "Limpieza completada",
        description: response.data.mensaje
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error limpiando archivos temporales",
        variant: "destructive"
      });
    }
  };

  const copyCommand = () => {
    navigator.clipboard.writeText('sudo systemctl restart lexduo-backend.service');
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(false), 2000);
    toast({
      title: "Comando copiado",
      description: "El comando de reinicio ha sido copiado al portapapeles"
    });
  };

  const resetForm = () => {
    setSelectedFile(null);
    setAnalisis(null);
    setResultado(null);
    setProgress(0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/configuracion')}>
          <ChevronLeft className="w-4 h-4 mr-2" />Volver
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sistema de Actualizaciones</h1>
          <p className="text-gray-600">Gestión de actualizaciones para entorno Plesk</p>
        </div>
      </div>

      {/* Info de versión actual */}
      <Card className="border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Versión actual del sistema</p>
                <p className="text-2xl font-bold text-gray-900">{versionActual?.version || "1.0.0"}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Última actualización</p>
              <p className="text-sm font-medium text-gray-700">
                {versionActual?.ultima_actualizacion 
                  ? new Date(versionActual.ultima_actualizacion).toLocaleString('es-ES')
                  : "Sin actualizaciones previas"}
              </p>
              <Badge variant="outline" className="mt-1">
                {versionActual?.total_actualizaciones || 0} actualizaciones aplicadas
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diagnóstico del entorno */}
      {diagnostico && (
        <Alert className={diagnostico.entorno?.is_plesk ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200"}>
          <Settings className="w-4 h-4" />
          <AlertTitle>Entorno detectado: {diagnostico.entorno?.tipo?.toUpperCase() || "Desconocido"}</AlertTitle>
          <AlertDescription>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div>
                <span className="font-medium">Backend:</span>{' '}
                <code className="bg-white px-1 rounded text-xs">{diagnostico.rutas?.base_dir?.ruta}</code>
                {diagnostico.rutas?.base_dir?.escritura ? 
                  <CheckCircle className="inline w-3 h-3 ml-1 text-green-600" /> : 
                  <AlertCircle className="inline w-3 h-3 ml-1 text-red-600" />
                }
              </div>
              <div>
                <span className="font-medium">Frontend:</span>{' '}
                <code className="bg-white px-1 rounded text-xs">{diagnostico.rutas?.httpdocs_dir?.ruta}</code>
                {diagnostico.rutas?.httpdocs_dir?.escritura ? 
                  <CheckCircle className="inline w-3 h-3 ml-1 text-green-600" /> : 
                  <AlertCircle className="inline w-3 h-3 ml-1 text-red-600" />
                }
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="aplicar">
            <Upload className="w-4 h-4 mr-2" />Aplicar Actualización
          </TabsTrigger>
          <TabsTrigger value="historial">
            <History className="w-4 h-4 mr-2" />Historial
          </TabsTrigger>
          <TabsTrigger value="backups">
            <FolderOpen className="w-4 h-4 mr-2" />Backups
          </TabsTrigger>
        </TabsList>

        {/* Tab: Aplicar Actualización */}
        <TabsContent value="aplicar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Subir Paquete de Actualización
              </CardTitle>
              <CardDescription>
                Selecciona un archivo ZIP con la estructura correcta: <code>backend/</code> y/o <code>httpdocs/</code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedFile ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept=".zip"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="update-file"
                    disabled={uploading}
                  />
                  <label htmlFor="update-file" className="cursor-pointer">
                    <FileArchive className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Arrastra un archivo o haz clic para seleccionar</p>
                    <p className="text-sm text-gray-400">Solo archivos .zip con estructura backend/ y/o httpdocs/</p>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Info del archivo seleccionado */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileArchive className="w-8 h-8 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={resetForm}>
                      Cambiar
                    </Button>
                  </div>

                  {/* Progress */}
                  {(uploading || applying) && (
                    <div className="space-y-2">
                      <Progress value={progress} className="h-2" />
                      <p className="text-sm text-gray-500 text-center">
                        {uploading ? "Analizando paquete..." : "Aplicando actualización..."}
                      </p>
                    </div>
                  )}

                  {/* Análisis del paquete */}
                  {analisis && !resultado && (
                    <div className="space-y-4">
                      <Alert className="bg-blue-50 border-blue-200">
                        <Info className="w-4 h-4 text-blue-600" />
                        <AlertDescription>
                          <strong>Versión:</strong> {analisis.version} | 
                          <strong> Archivos:</strong> {analisis.total_archivos} | 
                          <strong> Tamaño:</strong> {analisis.tamano_mb} MB
                        </AlertDescription>
                      </Alert>

                      {analisis.descripcion && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">{analisis.descripcion}</p>
                        </div>
                      )}

                      {analisis.warnings?.length > 0 && (
                        <Alert className="bg-yellow-50 border-yellow-200">
                          <AlertTriangle className="w-4 h-4 text-yellow-600" />
                          <AlertDescription>
                            {analisis.warnings.map((w, i) => (
                              <p key={i} className="text-yellow-700">{w}</p>
                            ))}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Componentes a actualizar */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {analisis.componentes.includes('backend') && (
                          <div className="p-4 border rounded-lg bg-green-50/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Server className="w-5 h-5 text-green-600" />
                              <span className="font-medium">Backend</span>
                              <Badge variant="outline" className="bg-white">
                                {analisis.archivos_backend?.length || 0} archivos
                              </Badge>
                            </div>
                            <div className="max-h-32 overflow-y-auto text-xs text-gray-500 space-y-1">
                              {(analisis.archivos_backend || []).slice(0, 10).map((f, i) => (
                                <p key={i} className="truncate font-mono">{f}</p>
                              ))}
                              {(analisis.archivos_backend?.length || 0) > 10 && (
                                <p className="text-blue-600">...y {analisis.archivos_backend.length - 10} más</p>
                              )}
                            </div>
                          </div>
                        )}
                        {analisis.componentes.includes('frontend') && (
                          <div className="p-4 border rounded-lg bg-purple-50/50">
                            <div className="flex items-center gap-2 mb-2">
                              <Monitor className="w-5 h-5 text-purple-600" />
                              <span className="font-medium">Frontend (httpdocs)</span>
                              <Badge variant="outline" className="bg-white">
                                {analisis.archivos_frontend?.length || 0} archivos
                              </Badge>
                            </div>
                            <div className="max-h-32 overflow-y-auto text-xs text-gray-500 space-y-1">
                              {(analisis.archivos_frontend || []).slice(0, 10).map((f, i) => (
                                <p key={i} className="truncate font-mono">{f}</p>
                              ))}
                              {(analisis.archivos_frontend?.length || 0) > 10 && (
                                <p className="text-blue-600">...y {analisis.archivos_frontend.length - 10} más</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Opciones */}
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <Checkbox
                          id="backup"
                          checked={crearBackup}
                          onCheckedChange={setCrearBackup}
                        />
                        <label htmlFor="backup" className="text-sm text-gray-700 cursor-pointer">
                          Crear copia de seguridad antes de actualizar (muy recomendado)
                        </label>
                      </div>

                      {/* Botón aplicar */}
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={handleAplicar}
                        disabled={applying}
                      >
                        {applying ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Aplicando actualización...</>
                        ) : (
                          <><ShieldCheck className="w-4 h-4 mr-2" />Aplicar Actualización</>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Resultado */}
                  {resultado && (
                    <div className="space-y-4">
                      <Alert className={resultado.exito ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}>
                        {resultado.exito ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        )}
                        <AlertTitle>{resultado.exito ? "Actualización Exitosa" : "Errores en la Actualización"}</AlertTitle>
                        <AlertDescription>
                          <p className={resultado.exito ? "text-green-700" : "text-red-700"}>
                            {resultado.mensaje}
                          </p>
                        </AlertDescription>
                      </Alert>

                      {/* Detalles de archivos actualizados */}
                      {resultado.archivos_actualizados?.length > 0 && (
                        <div className="p-4 bg-green-50 rounded-lg">
                          <p className="font-medium text-green-700 mb-2 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {resultado.archivos_actualizados.length} archivos actualizados correctamente
                          </p>
                          <div className="max-h-40 overflow-y-auto text-xs text-green-600 space-y-1">
                            {resultado.archivos_actualizados.slice(0, 20).map((f, i) => (
                              <p key={i} className="font-mono">
                                {typeof f === 'object' ? `${f.origen} → ${f.destino}` : f}
                              </p>
                            ))}
                            {resultado.archivos_actualizados.length > 20 && (
                              <p>...y {resultado.archivos_actualizados.length - 20} más</p>
                            )}
                          </div>
                        </div>
                      )}

                      {resultado.archivos_fallidos?.length > 0 && (
                        <div className="p-4 bg-red-50 rounded-lg">
                          <p className="font-medium text-red-700 mb-2 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            {resultado.archivos_fallidos.length} archivos con error
                          </p>
                          <div className="text-xs text-red-600 max-h-32 overflow-y-auto space-y-1">
                            {resultado.archivos_fallidos.map((f, i) => (
                              <p key={i} className="font-mono">
                                {f.archivo}: <span className="text-red-500">{f.error}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {resultado.backup_creado && (
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <FolderOpen className="w-4 h-4" />
                          Backup creado: <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">{resultado.backup_creado}</code>
                        </p>
                      )}

                      {/* Comando de reinicio */}
                      {resultado.reinicio_requerido && (
                        <Alert className="bg-amber-50 border-amber-200">
                          <Terminal className="w-4 h-4 text-amber-600" />
                          <AlertTitle className="text-amber-800">Reinicio del Backend Requerido</AlertTitle>
                          <AlertDescription className="text-amber-700">
                            <p className="mb-3">
                              Se han actualizado archivos del backend. Para que los cambios surtan efecto, 
                              ejecuta el siguiente comando en el servidor:
                            </p>
                            <div className="flex items-center gap-2 bg-gray-900 text-green-400 p-3 rounded-lg font-mono text-sm">
                              <code className="flex-1">sudo systemctl restart lexduo-backend.service</code>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={copyCommand}
                                className="text-white hover:bg-gray-700"
                              >
                                {copiedCommand ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            </div>
                          </AlertDescription>
                        </Alert>
                      )}

                      <Button variant="outline" onClick={resetForm} className="w-full">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir otra actualización
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instrucciones */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Estructura del Paquete ZIP
              </h3>
              <div className="bg-white p-4 rounded-lg border text-sm font-mono text-gray-600">
                <p className="text-gray-400 mb-2"># Estructura requerida:</p>
                <p>mi-actualizacion.zip/</p>
                <p className="pl-4">├── manifest.json <span className="text-gray-400">(opcional)</span></p>
                <p className="pl-4">├── backend/ <span className="text-gray-400">→ archivos Python</span></p>
                <p className="pl-8">├── routers/</p>
                <p className="pl-8">├── models_sql/</p>
                <p className="pl-8">└── server.py</p>
                <p className="pl-4">└── httpdocs/ <span className="text-gray-400">→ frontend compilado</span></p>
                <p className="pl-8">├── index.html</p>
                <p className="pl-8">└── static/</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Historial */}
        <TabsContent value="historial">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historial de Actualizaciones
                </CardTitle>
                <CardDescription>Registro de todas las actualizaciones aplicadas</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleLimpiarTemporales}>
                <Trash2 className="w-4 h-4 mr-2" />Limpiar temporales
              </Button>
            </CardHeader>
            <CardContent>
              {historial.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay actualizaciones registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {historial.slice().reverse().map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          item.tipo === 'restauracion' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          {item.tipo === 'restauracion' ? 
                            <RotateCcw className="w-5 h-5 text-amber-600" /> :
                            <Package className="w-5 h-5 text-blue-600" />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {item.tipo === 'restauracion' ? 'Restauración' : `Versión ${item.version}`}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {item.archivos_actualizados} archivos
                            </Badge>
                            {item.archivos_fallidos > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {item.archivos_fallidos} errores
                              </Badge>
                            )}
                            {item.entorno && (
                              <Badge variant="secondary" className="text-xs">
                                {item.entorno}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {item.archivo} • Por {item.usuario}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {new Date(item.fecha).toLocaleDateString('es-ES')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(item.fecha).toLocaleTimeString('es-ES')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Backups */}
        <TabsContent value="backups">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Copias de Seguridad
              </CardTitle>
              <CardDescription>
                Backups creados automáticamente antes de cada actualización
              </CardDescription>
            </CardHeader>
            <CardContent>
              {backups.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay backups disponibles</p>
                  <p className="text-sm mt-2">Se crearán automáticamente al aplicar actualizaciones</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {backups.map((backup, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <FolderOpen className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{backup.nombre}</p>
                          <p className="text-sm text-gray-500">
                            {backup.archivos} archivos
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            {new Date(backup.fecha).toLocaleDateString('es-ES')}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(backup.fecha).toLocaleTimeString('es-ES')}
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRestaurarBackup(backup.nombre)}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Restaurar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ActualizarSistema;
